import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, CreditCard, Loader2, ExternalLink, CalendarClock } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const planTitle = (user, plan) => {
  if (user.role !== "user") return "Accesso amministratore";
  return plan.type === "pro" ? "Aurora Pro" : "Prova gratuita";
};

export const BillingView = ({ user }) => {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/billing/info").then((r) => setInfo(r.data)); }, []);
  const plan = user.plan || {};
  const exp = plan.expires_at ? new Date(plan.expires_at) : null;
  const daysLeft = exp ? Math.max(0, Math.ceil((exp - Date.now()) / 86400000)) : 0;

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e, "Impossibile avviare il pagamento")); setBusy(false); }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-6" data-testid="billing-view">
      <p className="hud-tag mb-2">Piano</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">Abbonamento</h2>
      <div className="grid md:grid-cols-2 gap-5">
        <div className="hud-card p-6">
          <p className="text-xs font-mono uppercase tracking-widest text-slate-400">Stato attuale</p>
          <p className="font-heading text-3xl text-white mt-2" data-testid="billing-plan-type">
            {planTitle(user, plan)}
          </p>
          <div className="mt-4 flex items-center gap-2 text-sm">
            <CalendarClock size={16} className={user.subscription_active ? "text-emerald-300" : "text-rose-400"} />
            <span data-testid="billing-expiry">{user.subscription_active ? `Attivo fino al ${exp?.toLocaleDateString("it-IT")} · ${daysLeft} giorni rimasti` : "Scaduto"}</span>
          </div>
        </div>
        <div className="hud-card hud-corners p-6 border-amber-400/30">
          <div className="flex items-center gap-2 text-amber-300"><Crown size={18} /><span className="hud-tag !text-amber-300">Aurora Pro</span></div>
          <p className="font-heading text-4xl text-white mt-3">{info ? `${info.amount.toFixed(2).replace(".", ",")} ${info.currency}` : "…"}<span className="text-base text-slate-400"> / {info?.days || 30} giorni</span></p>
          <ul className="text-sm text-slate-300 mt-4 space-y-1.5">
            <li>• Tutti i modelli AI e l'assistente vocale</li><li>• Immagini, OCR e PDF illimitati*</li><li>• Supporto diretto e notifiche</li>
          </ul>
          <button onClick={pay} disabled={busy} data-testid="btn-checkout" className="btn-aurora w-full mt-6 rounded-xl py-3 text-sm uppercase flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CreditCard size={16} />} {user.subscription_active ? "Estendi di 30 giorni" : "Attiva ora"}
          </button>
          {info?.payment_link && (
            <a href={info.payment_link} target="_blank" rel="noopener noreferrer" data-testid="btn-external-payment" className="btn-ghost-hud w-full mt-3 rounded-xl py-2.5 text-xs flex items-center justify-center gap-2">
              Paga con link esterno (Stripe/Wise) <ExternalLink size={13} />
            </a>
          )}
          <p className="text-[11px] text-slate-500 mt-3">Pagamento sicuro gestito da Stripe. Riceverai un promemoria email prima della scadenza. *Uso conforme.</p>
        </div>
      </div>
    </div>
  );
};
